import { useEffect, useState } from "react";
import { fetchAllPrograms, fetchProgram } from "./programsAPI";
import {
  CreateProgramInput,
  CreateProgramMembershipInput,
  UpdateProgramInput,
  Program,
  ProgramMembership
} from "../../API";
import { QueryStatus } from "../../app/types";
import {
  createProgram as createProgramAPI,
  createProgramMembership as createProgramMembershipAPI,
  updateProgram as updateProgramAPI
} from "./programsAPI";
import { useDispatch } from "react-redux";
import {
  selectPrograms,
  selectProgramsError,
  selectProgramsStatus,
  setPrograms,
  setProgramsError,
  setProgramsStatus
} from "./programsSlice";
import { useAppSelector } from "../../app/hooks";

export const useProgramById = (id?: string) => {
  const [program, setProgram] = useState<Program | null>();
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getProgram = async (id: string): Promise<Program | null> => {
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchProgram(id);
      setProgram(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      console.error("error:", error);
      setStatus("failed");
      setError(`Error loading programs: ${error}`);
    }
    return null;
  };

  useEffect(() => {
    if (id) {
      getProgram(id);
    }
  }, [id]);

  return {
    program,
    status,
    error,
    getProgram
  };
};

export const useAllPrograms = () => {
  const allPrograms = useAppSelector(selectPrograms);
  const status = useAppSelector(selectProgramsStatus);
  const error = useAppSelector(selectProgramsError);

  const dispatch = useDispatch();

  const getAllPrograms = async () => {
    // If already loaded or currently loading, don't reload
    if (status === "loading" || status === "succeeded") {
      return;
    }

    dispatch(setProgramsStatus("loading"));
    dispatch(setProgramsError(undefined));

    try {
      const results = await fetchAllPrograms();
      dispatch(setPrograms(results));
      dispatch(setProgramsStatus("succeeded"));
    } catch (error) {
      console.error("Error loading programs:", error);
      dispatch(setProgramsStatus("failed"));
      dispatch(setProgramsError(`Error loading programs: ${error}`));
    }
  };

  return {
    programs: allPrograms,
    status,
    error,
    getAllPrograms
  };
};

export const useCreateProgram = () => {
  const [newProgram, setNewProgram] = useState<Program | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const createProgram = async (
    input: CreateProgramInput & { trainingStageId?: string | null }
  ) => {
    setStatus("loading");
    setError(null);

    try {
      const creationResult = await createProgramAPI(input);
      setNewProgram(creationResult);
      setStatus("succeeded");
      return creationResult;
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    newProgram,
    status,
    error,
    createProgram
  };
};

export const useUpdateProgram = () => {
  const [updatedProgram, setUpdatedProgram] = useState<Program | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const updateProgram = async (
    input: UpdateProgramInput & { trainingStageId?: string | null }
  ) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await updateProgramAPI(input);
      setUpdatedProgram(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    updatedProgram,
    status,
    error,
    updateProgram
  };
};

export const useCreateProgramMembership = () => {
  const [newProgramMembership, setNewProgramMembership] =
    useState<ProgramMembership | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const createProgramMembership = async (
    input: CreateProgramMembershipInput
  ) => {
    setStatus("loading");
    setError(null);
    try {
      const creationResult = await createProgramMembershipAPI(input);
      setNewProgramMembership(creationResult);
      setStatus("succeeded");
      return creationResult;
    } catch (error) {
      const errorString = `${error}`;
      console.error("error creating program membership:", error);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    newProgramMembership,
    status,
    error,
    createProgramMembership
  };
};

/**
 * Display label for a program in a picker:
 * "Henry Ford Hospital (Institution) > General Surgery (Program)".
 *
 * `program.name` is a generic specialty name, so dozens of programs share
 * one across institutions — a picker showing the bare name lists "General
 * Surgery" a dozen times with no way to tell them apart.
 *
 * The format is the one the Users page program pickers already use, copied
 * verbatim so every program picker in the app reads the same way:
 * `SysAdminsUsersTable.tsx`, `ProgramAdminsUsersTable.tsx`, and the
 * `UserInvitationsBulkInvite` confirmation banner all build this exact
 * string inline. `ProgramAdminsUsersTable.tsx:476` carries a TODO asking
 * for somewhere to share it from — this is that place, and those three
 * sites can be switched over to it.
 *
 * Network is deliberately NOT part of this label: a program can belong to
 * several networks (`networks.items` is a list), so there is no single
 * network to name, and no existing surface qualifies a program that way.
 *
 * `institution` is NOT loaded separately — `programs-list` LEFT JOINs it and
 * returns it on the same payload as the program. It comes back `null` only
 * when the program carries no `institution_id`, or when its institution row
 * is soft-deleted (the join condition includes `notDeleted(institution)`).
 * There is no window where a program is present and its institution is still
 * on the way, so there is nothing to wait for.
 *
 * When it is null but `institutionId` is set, the institution existed and was
 * deleted. Nothing in the app retains deleted institutions, so the label
 * cannot name which one — it says "Deleted institution" and stops there.
 * Who should see such a program at all is `programsVisibleTo`'s decision,
 * not this function's.
 *
 * Neither case occurs in stg today (0 of 686 live programs), so both branches
 * are defensive. The bare-name one exists because the call sites this replaced
 * interpolated `program.institution?.name` unguarded and would render the
 * literal string "undefined (Institution)".
 */
type LabelableProgram = {
  name?: string | null;
  institutionId?: string | null;
  institution?: { name?: string | null } | null;
};

export const programLabel = (program: LabelableProgram): string => {
  const name = program.name?.trim() ?? "";
  const institutionName = program.institution?.name?.trim();
  if (institutionName) {
    return `${institutionName} (Institution) > ${name} (Program)`;
  }
  // Had an institution once; it is soft-deleted, and its name is not
  // retrievable from anywhere the client can reach.
  if (program.institutionId) {
    return `Deleted institution (Institution) > ${name} (Program)`;
  }
  return name;
};

/**
 * The programs a picker should offer, given who is looking.
 *
 * A program whose institution has been deleted is still a real, selectable
 * program, but it is only meaningful to someone who can act across
 * institutions. A SystemAdmin sees it (labelled "Deleted institution", so the
 * state is visible rather than silent); everyone else — ProgramAdmin
 * included — does not, because for them a program under a deleted institution
 * is an artefact they cannot reason about or repair.
 *
 * Programs that never had an institution (`institutionId` null) are treated
 * the same way: unresolvable for a non-SystemAdmin, so hidden.
 */
export const programsVisibleTo = <T extends LabelableProgram>(
  programs: T[],
  isSysAdmin: boolean
): T[] =>
  isSysAdmin
    ? programs
    : programs.filter((p) => !!p.institution?.name?.trim());

/**
 *
 * @param programs - The programs to sort
 * @param sortByInstitution - a boolean that, if true, will first sort by Institution name and then Program name
 * @returns
 */
export const alphabetizePrograms = <
  T extends {
    name?: string | null;
    institution?: { name?: string | null } | null;
  }
>(
  programs: T[],
  sortByInstitution = false
): T[] => {
  return [...programs].sort((a, b) => {
    const institutionA = a.institution?.name?.toLowerCase();
    const institutionB = b.institution?.name?.toLowerCase();
    const programNameA = a.name?.toLowerCase();
    const programNameB = b.name?.toLowerCase();
    if (programNameA && programNameB && institutionA && institutionB) {
      if (!sortByInstitution || institutionA === institutionB) {
        if (programNameA < programNameB) {
          return -1;
        }
        if (programNameA > programNameB) {
          return 1;
        }
      } else if (institutionA > institutionB) {
        return 1;
      } else if (institutionA < institutionB) {
        return -1;
      }
    }
    return 0;
  });
};

export const ProgramFormErrors = {
  EmptyProgramName: "Please enter a value for the program name.",
  NoInstitution:
    "There is no institution currently selected. Please select an institution to use or contact your administrator.",
  NoNetworks:
    "At least one network must be selected for the program."
};

export const ProgramNetworkErrors = {
  FetchAllProgramsError:
    "Error fetching programs. Please try again or contact your administrator.",
  FetchProgramError:
    "Error fetching program. Please try again or contact your administrator.",
  ProgramNotFound: "Program not found!",
  CreateProgramError:
    "There was an error creating this program. Please try again or contact your administrator.",
  CreateProgramMembershipError:
    "There was an error creating the program membership. Please try again or contact your administrator."
};

export const ProgramNotifications = {
  NoPrograms: "No programs to display.",
  NoProgramsInInstitution: "The selected institution has no programs."
};
